import React, { useState, useEffect, useCallback } from 'react';
import UploadXLS from './CreateItem';
import UploadStatus from './UploadStatus';
import InventorySync from './InventorySync';
import Automation from './Automation';
import PalletNights from './PalletNights';
import BarcodeGenerator from './BarcodeGenerator';
import './Dashboard.css';

interface DashboardProps {
  onLogout: () => void;
}

interface ZohoInvoice {
  invoice_id: string;
  invoice_number: string;
  customer_name: string;
  date: string;
  total: number;
  status: string;
  created_time: string;
  custom_fields?: Array<{
    label: string;
    value: string;
  }>;
}

const Dashboard: React.FC<DashboardProps> = ({ onLogout }) => {
  const [invoices, setInvoices] = useState<ZohoInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'invoices' | 'sync' | 'automation' | 'palletNights' | 'barcodes'>('automation');
  const [activeInvoiceSubTab, setActiveInvoiceSubTab] = useState<'upload' | 'invoices' | 'status'>('upload');
  const [refreshing, setRefreshing] = useState(false);

  const fetchInvoices = useCallback(async () => {
    try {
      setRefreshing(true);
      
      
      setInvoices([]); 
      setError('');
    } catch (err: any) {
      console.error('Error fetching invoices:', err);
      setError(err.response?.data?.message || 'Неуспешно зареждане на фактурите');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    
    fetchInvoices();
  }, [fetchInvoices]);

  const handleUploadComplete = () => {
    fetchInvoices();
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>
            <div className="inter-bell-logo">
              <div className="logo-text">
                <span className="company-name">INTER BELL</span>
                <span className="company-subtitle">INVENTORY MANAGEMENT</span>
              </div>
            </div>
          </h1>
          <div className="header-actions">
            <a
              href="https://inventory.zoho.eu/app/20103181543#/inventory/items?filter_by=Status.All&per_page=200&sort_column=name&sort_order=A"
              target="_blank"
              rel="noopener noreferrer"
              className="zoho-inventory-button"
            >
              <span className="zoho-icon">📦</span>
              Zoho Inventory
            </a>

            <button onClick={onLogout} className="logout-button">
              Изход
            </button>
          </div>
        </div>
      </header>

      <nav className="dashboard-nav">
        <button 
          className={`nav-button ${activeTab === 'automation' ? 'active' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveTab('automation');
          }}
        >
          Автоматизация
        </button>
        <button 
          className={`nav-button ${activeTab === 'sync' ? 'active' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveTab('sync');
          }}
        >
          Синхронизация на Инвентар
        </button>
        <button 
          className={`nav-button ${activeTab === 'palletNights' ? 'active' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveTab('palletNights');
          }}
        >
          Палетни Нощувки
        </button>
        <button 
          className={`nav-button ${activeTab === 'barcodes' ? 'active' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveTab('barcodes');
          }}
        >
          Баркодове
        </button>
        <button 
          className={`nav-button ${activeTab === 'invoices' ? 'active' : ''} disabled`}
          disabled={true}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            
          }}
        >
          Фактури ({invoices.length})
        </button>
      </nav>

      <main className="dashboard-content">
        {error && (
          <div className="error-banner">
            <p>{error}</p>
            <button onClick={() => setError('')}>×</button>
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="invoices-section">
            {}
            <nav className="sub-nav">
              <button 
                className={`sub-nav-button ${activeInvoiceSubTab === 'upload' ? 'active' : ''}`}
                onClick={() => setActiveInvoiceSubTab('upload')}
              >
                Качване на Файлове
              </button>
              <button 
                className={`sub-nav-button ${activeInvoiceSubTab === 'invoices' ? 'active' : ''}`}
                onClick={() => setActiveInvoiceSubTab('invoices')}
              >
                Списък Фактури
              </button>
              <button 
                className={`sub-nav-button ${activeInvoiceSubTab === 'status' ? 'active' : ''}`}
                onClick={() => setActiveInvoiceSubTab('status')}
              >
                Статус на Качванията
              </button>
            </nav>

            {}
            {activeInvoiceSubTab === 'upload' && (
              <UploadXLS onUploadComplete={handleUploadComplete} />
            )}

            {activeInvoiceSubTab === 'invoices' && (
              <div className="invoice-list">
                <h2>Списък Фактури</h2>
                {loading ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Зареждане на фактури...</p>
                  </div>
                ) : (
                  <div className="invoices-grid">
                    {invoices.length === 0 ? (
                      <div className="no-invoices">
                        <p>Няма намерени фактури</p>
                      </div>
                    ) : (
                      invoices.map((invoice) => (
                        <div key={invoice.invoice_id} className="invoice-card">
                          <div className="invoice-header">
                            <h3>{invoice.invoice_number}</h3>
                            <span className={`status ${invoice.status.toLowerCase()}`}>
                              {invoice.status}
                            </span>
                          </div>
                          <div className="invoice-details">
                            <div className="detail-row">
                              <span className="label">Клиент:</span>
                              <span className="value">{invoice.customer_name || 'Н/П'}</span>
                            </div>
                            <div className="detail-row">
                              <span className="label">Дата:</span>
                              <span className="value">{invoice.date}</span>
                            </div>
                            <div className="detail-row">
                              <span className="label">Общо:</span>
                              <span className="value">${invoice.total?.toFixed(2) || '0.00'}</span>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {activeInvoiceSubTab === 'status' && (
              <UploadStatus />
            )}
          </div>
        )}

        {activeTab === 'sync' && (
          <InventorySync />
        )}

        {activeTab === 'automation' && (
          <Automation />
        )}

        {activeTab === 'palletNights' && (
          <PalletNights />
        )}

        {activeTab === 'barcodes' && (
          <BarcodeGenerator />
        )}
      </main>
    </div>
  );
};

export default Dashboard; 